import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { supabase } from '@/lib/supabase';

const TABS = ['Genel Bilgiler', 'Yönlendirme Geçmişi', 'Deneme Dersleri', 'Aktif Dersler', 'Ödeme Geçmişi', 'Öğretmen Notları', 'Veli Görüşme Notları'];

interface Student {
  id: string; ogrenci_adi: string; veli_adi: string | null; telefon: string | null;
  sehir: string | null; sinif: string | null; branslar: string[] | null;
  ders_tipi: string | null; aktif: boolean; atanan_ogretmen_id: string | null;
  toplam_ders_sayisi: number | null; toplam_odeme: number | null;
  son_ders_tarihi: string | null; notlar: string | null; olusturma_tarihi: string;
}

interface Teacher { id: string; ad_soyad: string; branslar: string[] | null; telefon: string | null; }
interface Yonlendirme {
  id: string; brans: string | null; deneme_tarihi: string | null;
  durum: string | null; olusturma_tarihi: string;
  ogretmen?: { ad_soyad: string; telefon: string | null };
}
interface Trial {
  id: string; planlanan_tarih: string | null; planlanan_saat: string | null;
  brans: string | null; durum: string | null; sonuc: string | null;
  gerceklesen_sure: string | null; veli_memnun: boolean | null;
  ogretmen_degerlendirme_notu: string | null;
  ogretmen?: { ad_soyad: string };
}
interface Lesson {
  id: string; ders_tarihi: string; brans: string | null; islened_konu: string | null;
  islenen_konu: string | null; ders_durumu: string | null; ders_suresi: number | null;
  odev_verildi: boolean | null;
  ogretmen?: { ad_soyad: string };
}
interface Payment {
  id: string; dekont_tarihi: string; tutar: number; kac_derslik: number | null;
  odeme_durumu: string | null; aciklama: string | null;
}
interface LessonNote {
  id: string; not_turu: string | null; notlar: string | null;
  ders_tarihi: string | null; olusturma_tarihi: string;
  ogretmen?: { ad_soyad: string };
}
interface VeliNot {
  id: string; gorusme_tarihi: string | null; gorusme_konusu: string | null;
  notlar: string | null; olusturma_tarihi: string;
  olusturan?: { ad_soyad: string };
}

const statusBadge: Record<string, string> = {
  Aktif: 'bg-green-100 text-green-700',
  Pasif: 'bg-red-100 text-red-700',
  Takipte: 'bg-yellow-100 text-yellow-700',
  Yapıldı: 'bg-green-100 text-green-700',
  Planlandı: 'bg-blue-100 text-blue-700',
  Ertelendi: 'bg-yellow-100 text-yellow-700',
  İptal: 'bg-red-100 text-red-700',
  'Öğrenci Katılmadı': 'bg-orange-100 text-orange-700',
  Onaylandı: 'bg-green-100 text-green-700',
  'Dekont Bekleniyor': 'bg-yellow-100 text-yellow-700',
  'Aktif Derse Döndü': 'bg-green-100 text-green-700',
  Kararsız: 'bg-yellow-100 text-yellow-700',
  'Devam Etmeyecek': 'bg-red-100 text-red-700',
  Olumlu: 'bg-green-100 text-green-700',
  Olumsuz: 'bg-red-100 text-red-700',
};

function dbToLessonType(db: string | null): string {
  switch (db) {
    case 'bireysel_online': return 'Birebir Online';
    case 'yuz_yuze': return 'Yüz Yüze';
    case 'grup': return 'Grup Dersi';
    case 'kpss': return 'KPSS';
    default: return db || 'Birebir Online';
  }
}

export default function OgrenciDetayPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState(0);
  const [student, setStudent] = useState<Student | null>(null);
  const [teacher, setTeacher] = useState<Teacher | null>(null);
  const [yonlendirmeler, setYonlendirmeler] = useState<Yonlendirme[]>([]);
  const [trials, setTrials] = useState<Trial[]>([]);
  const [lessons, setLessons] = useState<Lesson[]>([]);
  const [payments, setPayments] = useState<Payment[]>([]);
  const [notes, setNotes] = useState<LessonNote[]>([]);
  const [veliNotlari, setVeliNotlari] = useState<VeliNot[]>([]);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState('');

  const showToast = (msg: string) => { setToast(msg); setTimeout(() => setToast(''), 3000); };

  const fetchAll = useCallback(async () => {
    if (!id) return;
    setLoading(true);

    const [stuRes, yonRes, trialRes, lesRes, payRes, noteRes, veliRes] = await Promise.all([
      supabase.from('egtpanel_ogrenciler').select('*').eq('id', id).maybeSingle(),
      supabase.from('egtpanel_yonlendirmeler').select('id, brans, deneme_tarihi, durum, olusturma_tarihi, ogretmen:egtpanel_ogretmenler(ad_soyad, telefon)').eq('ogrenci_id', id).order('olusturma_tarihi', { ascending: false }),
      supabase.from('egtpanel_deneme_dersleri').select('id, planlanan_tarih, planlanan_saat, brans, durum, sonuc, gerceklesen_sure, veli_memnun, ogretmen_degerlendirme_notu, ogretmen:egtpanel_ogretmenler(ad_soyad)').eq('ogrenci_id', id).order('planlanan_tarih', { ascending: false }),
      supabase.from('egtpanel_aktif_dersler').select('id, ders_tarihi, brans, islenen_konu, ders_durumu, ders_suresi, odev_verildi, ogretmen:egtpanel_ogretmenler(ad_soyad)').eq('ogrenci_id', id).order('ders_tarihi', { ascending: false }),
      supabase.from('egtpanel_odemeler').select('id, dekont_tarihi, tutar, kac_derslik, odeme_durumu, aciklama').eq('ogrenci_id', id).order('dekont_tarihi', { ascending: false }),
      supabase.from('egtpanel_ders_notlari').select('id, not_turu, notlar, ders_tarihi, olusturma_tarihi, ogretmen:egtpanel_ogretmenler(ad_soyad)').eq('ogrenci_id', id).order('olusturma_tarihi', { ascending: false }),
      supabase.from('egtpanel_veli_gorusme_notlari').select('id, gorusme_tarihi, gorusme_konusu, notlar, olusturma_tarihi').eq('ogrenci_id', id).order('olusturma_tarihi', { ascending: false }),
    ]);

    const stu = stuRes.data as Student | null;
    setStudent(stu);

    if (stu?.atanan_ogretmen_id) {
      const { data: t } = await supabase.from('egtpanel_ogretmenler').select('id, ad_soyad, branslar, telefon').eq('id', stu.atanan_ogretmen_id).maybeSingle();
      setTeacher(t);
    } else {
      setTeacher(null);
    }

    setYonlendirmeler((yonRes.data || []) as unknown as Yonlendirme[]);
    setTrials((trialRes.data || []) as unknown as Trial[]);
    setLessons((lesRes.data || []) as unknown as Lesson[]);
    setPayments(payRes.data || []);
    setNotes((noteRes.data || []) as unknown as LessonNote[]);
    setVeliNotlari(veliRes.data || []);
    setLoading(false);
  }, [id]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  if (loading) return (
    <div className="p-6 flex items-center justify-center py-32">
      <i className="ri-loader-4-line text-3xl text-slate-300 animate-spin" />
    </div>
  );

  if (!student) return (
    <div className="p-6 flex flex-col items-center justify-center h-64">
      <i className="ri-user-unfollow-line text-5xl text-slate-200 mb-3" />
      <p className="text-slate-400">Öğrenci bulunamadı</p>
      <button onClick={() => navigate('/admin/ogrenciler')} className="mt-4 text-[#1e3a5f] text-sm font-medium cursor-pointer">← Listeye Dön</button>
    </div>
  );

  const studentStatus = student.aktif ? 'Aktif' : 'Pasif';
  const totalApproved = payments.filter((p) => p.odeme_durumu === 'Onaylandı').reduce((a, p) => a + Number(p.tutar), 0);

  // Add veli gorusme notu
  const handleAddVeliNot = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const fd = new FormData(e.currentTarget);
    const { error } = await supabase.from('egtpanel_veli_gorusme_notlari').insert({
      ogrenci_id: id,
      gorusme_tarihi: fd.get('date') as string || null,
      gorusme_konusu: fd.get('subject') as string || null,
      notlar: fd.get('notes') as string || null,
    });
    if (!error) {
      showToast('Not eklendi!');
      fetchAll();
      (e.target as HTMLFormElement).reset();
    } else { showToast('Hata: Not eklenemedi.'); }
  };

  return (
    <div className="p-6 space-y-6">
      {toast && <div className="fixed top-4 right-4 z-50 bg-[#1e3a5f] text-white px-5 py-3 rounded-xl text-sm font-medium">{toast}</div>}

      <div>
        <button onClick={() => navigate('/admin/ogrenciler')} className="flex items-center gap-2 text-slate-500 hover:text-slate-800 text-sm mb-4 transition-colors cursor-pointer">
          <i className="ri-arrow-left-line" /> Öğrenci Listesine Dön
        </button>
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 flex items-center justify-center rounded-2xl bg-[#1e3a5f] text-white text-xl font-bold">
              {student.ogrenci_adi.charAt(0)}
            </div>
            <div>
              <h1 className="text-2xl font-bold text-slate-800">{student.ogrenci_adi}</h1>
              <p className="text-slate-500 text-sm">{student.sinif} {student.branslar?.length ? `· ${student.branslar.join(', ')}` : ''}</p>
            </div>
          </div>
          <span className={`px-3 py-1.5 rounded-full text-sm font-medium ${statusBadge[studentStatus]}`}>{studentStatus}</span>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          { label: 'Toplam Ders', val: student.toplam_ders_sayisi ?? lessons.length, icon: 'ri-book-open-line' },
          { label: 'Toplam Ödeme', val: student.toplam_odeme ? `₺${Number(student.toplam_odeme).toLocaleString('tr-TR')}` : '-', icon: 'ri-money-dollar-circle-line' },
          { label: 'Son Ders', val: student.son_ders_tarihi || (lessons[0]?.ders_tarihi ?? '-'), icon: 'ri-calendar-line' },
          { label: 'Öğretmen', val: teacher?.ad_soyad || 'Atanmadı', icon: 'ri-user-star-line' },
        ].map((s) => (
          <div key={s.label} className="bg-white rounded-xl border border-slate-100 p-4">
            <div className="flex items-center gap-2 mb-2">
              <i className={`${s.icon} text-slate-400`} />
              <p className="text-xs text-slate-500">{s.label}</p>
            </div>
            <p className="text-base font-semibold text-slate-800 truncate">{s.val}</p>
          </div>
        ))}
      </div>

      <div className="bg-white rounded-xl border border-slate-100">
        <div className="border-b border-slate-100 overflow-x-auto">
          <div className="flex">
            {TABS.map((tab, i) => (
              <button key={tab} onClick={() => setActiveTab(i)}
                className={`px-4 py-3 text-sm font-medium whitespace-nowrap border-b-2 transition-colors cursor-pointer ${activeTab === i ? 'border-[#1e3a5f] text-[#1e3a5f]' : 'border-transparent text-slate-500 hover:text-slate-700'}`}>
                {tab}
              </button>
            ))}
          </div>
        </div>

        <div className="p-6">
          {activeTab === 0 && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-3">
                <h3 className="text-sm font-semibold text-slate-700 uppercase tracking-wide">Öğrenci Bilgileri</h3>
                {[
                  { label: 'Öğrenci Adı', val: student.ogrenci_adi },
                  { label: 'Veli Adı', val: student.veli_adi || '-' },
                  { label: 'Telefon', val: student.telefon || '-' },
                  { label: 'Sınıf', val: student.sinif || '-' },
                  { label: 'Şehir', val: student.sehir || '-' },
                  { label: 'Ders Tipi', val: dbToLessonType(student.ders_tipi) },
                  { label: 'Kayıt Tarihi', val: student.olusturma_tarihi?.split('T')[0] || '-' },
                ].map((r) => (
                  <div key={r.label} className="flex items-center gap-3 py-2 border-b border-slate-50">
                    <span className="text-xs text-slate-400 w-28 shrink-0">{r.label}</span>
                    <span className="text-sm text-slate-700 font-medium">{r.val}</span>
                  </div>
                ))}
              </div>
              <div className="space-y-3">
                <h3 className="text-sm font-semibold text-slate-700 uppercase tracking-wide">Ders Bilgileri</h3>
                {[
                  { label: 'Branşlar', val: student.branslar?.join(', ') || '-' },
                  { label: 'Atanan Öğretmen', val: teacher?.ad_soyad || 'Atanmadı' },
                  { label: 'Toplam Ders', val: `${student.toplam_ders_sayisi ?? lessons.length} ders` },
                  { label: 'Onaylı Tahsilat', val: `₺${totalApproved.toLocaleString('tr-TR')}` },
                  { label: 'Son Ders', val: student.son_ders_tarihi || lessons[0]?.ders_tarihi || '-' },
                ].map((r) => (
                  <div key={r.label} className="flex items-center gap-3 py-2 border-b border-slate-50">
                    <span className="text-xs text-slate-400 w-28 shrink-0">{r.label}</span>
                    <span className="text-sm text-slate-700 font-medium">{r.val}</span>
                  </div>
                ))}
                {student.notlar && (
                  <div className="bg-amber-50 rounded-lg p-3 text-sm text-amber-800 mt-2">
                    <i className="ri-sticky-note-line mr-1" />{student.notlar}
                  </div>
                )}
              </div>
            </div>
          )}

          {activeTab === 1 && (
            <div className="space-y-3">
              {yonlendirmeler.length === 0 ? (
                <div className="text-center py-12">
                  <i className="ri-user-add-line text-4xl text-slate-200 block mb-2" />
                  <p className="text-slate-400 text-sm">Henüz yönlendirme yapılmamış</p>
                </div>
              ) : yonlendirmeler.map((y) => {
                const ogr = y.ogretmen as { ad_soyad: string; telefon: string | null } | undefined;
                return (
                  <div key={y.id} className="border border-slate-100 rounded-xl p-4">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 flex items-center justify-center rounded-full bg-[#1e3a5f] text-white text-sm font-bold shrink-0">
                          {ogr?.ad_soyad?.charAt(0) || '?'}
                        </div>
                        <div>
                          <p className="font-medium text-slate-800">{ogr?.ad_soyad || '—'}</p>
                          <p className="text-xs text-slate-400">{ogr?.telefon || ''}</p>
                        </div>
                      </div>
                      <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium ${statusBadge[y.durum || ''] || 'bg-slate-100 text-slate-500'}`}>{y.durum || '—'}</span>
                    </div>
                    <div className="text-xs text-slate-500">
                      {y.brans && <span>Branş: {y.brans} · </span>}
                      {y.deneme_tarihi && <span>Deneme: {y.deneme_tarihi} · </span>}
                      <span>Tarih: {y.olusturma_tarihi?.split('T')[0]}</span>
                    </div>
                  </div>
                );
              })}
              {teacher && (
                <div className="bg-green-50 rounded-xl p-4 flex items-center gap-4 mt-4">
                  <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-green-600 text-white font-bold">{teacher.ad_soyad.charAt(0)}</div>
                  <div>
                    <p className="font-medium text-green-800">{teacher.ad_soyad}</p>
                    <p className="text-xs text-green-600">{teacher.branslar?.join(', ')} · {teacher.telefon}</p>
                  </div>
                  <span className="ml-auto text-xs bg-green-200 text-green-800 px-2 py-0.5 rounded-full">Aktif Öğretmen</span>
                </div>
              )}
            </div>
          )}

          {activeTab === 2 && (
            <div>
              {trials.length === 0 ? (
                <div className="text-center py-12">
                  <i className="ri-calendar-todo-line text-4xl text-slate-200 block mb-2" />
                  <p className="text-slate-400 text-sm">Deneme dersi kaydı yok</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {trials.map((t) => (
                    <div key={t.id} className="border border-slate-100 rounded-xl p-4">
                      <div className="flex items-center justify-between mb-2">
                        <span className="font-medium text-slate-800">{t.planlanan_tarih || '—'} {t.planlanan_saat?.slice(0, 5) || ''}</span>
                        <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium ${statusBadge[t.durum || ''] || 'bg-slate-100 text-slate-600'}`}>{t.durum || '—'}</span>
                      </div>
                      <div className="text-sm text-slate-600 space-y-0.5">
                        <div>Öğretmen: <span className="font-medium">{(t.ogretmen as { ad_soyad: string } | undefined)?.ad_soyad || '—'}</span></div>
                        <div>Süre: {t.gerceklesen_sure || '—'} · Sonuç: <span className="font-medium">{t.sonuc || '—'}</span> · Veli Memnun: {t.veli_memnun == null ? '—' : t.veli_memnun ? 'Evet' : 'Hayır'}</div>
                        {t.ogretmen_degerlendirme_notu && <div className="text-slate-500 italic text-xs mt-1">"{t.ogretmen_degerlendirme_notu}"</div>}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {activeTab === 3 && (
            <div>
              {lessons.length === 0 ? (
                <div className="text-center py-12">
                  <i className="ri-book-open-line text-4xl text-slate-200 block mb-2" />
                  <p className="text-slate-400 text-sm">Ders kaydı yok</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead><tr className="border-b border-slate-100 text-xs text-slate-500">
                      <th className="text-left pb-2 pr-4">Tarih</th>
                      <th className="text-left pb-2 pr-4">Öğretmen</th>
                      <th className="text-left pb-2 pr-4">Branş</th>
                      <th className="text-left pb-2 pr-4">Konu</th>
                      <th className="text-left pb-2 pr-4">Süre</th>
                      <th className="text-left pb-2 pr-4">Ödev</th>
                      <th className="text-left pb-2">Durum</th>
                    </tr></thead>
                    <tbody className="divide-y divide-slate-50">
                      {lessons.map((l) => (
                        <tr key={l.id}>
                          <td className="py-2.5 pr-4 whitespace-nowrap">{l.ders_tarihi}</td>
                          <td className="py-2.5 pr-4 whitespace-nowrap">{(l.ogretmen as { ad_soyad: string } | undefined)?.ad_soyad || '—'}</td>
                          <td className="py-2.5 pr-4 whitespace-nowrap">{l.brans || '—'}</td>
                          <td className="py-2.5 pr-4 text-slate-600 max-w-[130px] truncate">{l.islenen_konu || '—'}</td>
                          <td className="py-2.5 pr-4 whitespace-nowrap">{l.ders_suresi ? `${l.ders_suresi} dk` : '—'}</td>
                          <td className="py-2.5 pr-4 whitespace-nowrap">
                            {l.odev_verildi ? <span className="bg-green-50 text-green-700 px-1.5 py-0.5 rounded text-xs">Verildi</span> : <span className="text-slate-400 text-xs">Yok</span>}
                          </td>
                          <td className="py-2.5"><span className={`px-2 py-0.5 rounded-full text-xs font-medium ${statusBadge[l.ders_durumu || ''] || 'bg-slate-100 text-slate-600'}`}>{l.ders_durumu || '—'}</span></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {activeTab === 4 && (
            <div>
              {payments.length === 0 ? (
                <div className="text-center py-12">
                  <i className="ri-money-dollar-circle-line text-4xl text-slate-200 block mb-2" />
                  <p className="text-slate-400 text-sm">Ödeme kaydı yok</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {payments.map((p) => (
                    <div key={p.id} className="border border-slate-100 rounded-xl p-4 flex items-center justify-between">
                      <div>
                        <div className="font-medium text-slate-800">₺{Number(p.tutar).toLocaleString('tr-TR')} — {p.kac_derslik || '-'} ders</div>
                        <div className="text-xs text-slate-400 mt-0.5">{p.dekont_tarihi} {p.aciklama ? `· ${p.aciklama}` : ''}</div>
                      </div>
                      <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${statusBadge[p.odeme_durumu || ''] || 'bg-slate-100 text-slate-600'}`}>{p.odeme_durumu || '—'}</span>
                    </div>
                  ))}
                  <div className="bg-slate-50 rounded-xl p-4 flex items-center justify-between">
                    <span className="text-sm font-semibold text-slate-600">Toplam Onaylı Tahsilat</span>
                    <span className="text-lg font-bold text-slate-800">₺{totalApproved.toLocaleString('tr-TR')}</span>
                  </div>
                </div>
              )}
            </div>
          )}

          {activeTab === 5 && (
            <div>
              {notes.length === 0 ? (
                <div className="text-center py-12">
                  <i className="ri-sticky-note-line text-4xl text-slate-200 block mb-2" />
                  <p className="text-slate-400 text-sm">Henüz not eklenmemiş</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {notes.map((n) => (
                    <div key={n.id} className="border border-slate-100 rounded-xl p-4">
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-medium text-slate-700">{(n.ogretmen as { ad_soyad: string } | undefined)?.ad_soyad || '—'}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          {n.not_turu && <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${statusBadge[n.not_turu] || 'bg-slate-100 text-slate-600'}`}>{n.not_turu}</span>}
                          <span className="text-xs text-slate-400">{n.ders_tarihi || n.olusturma_tarihi?.split('T')[0]}</span>
                        </div>
                      </div>
                      <p className="text-sm text-slate-600">{n.notlar || '—'}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {activeTab === 6 && (
            <div className="space-y-4">
              <form onSubmit={handleAddVeliNot} className="bg-slate-50 rounded-xl p-4 space-y-3">
                <h4 className="text-sm font-semibold text-slate-700">Yeni Veli Görüşme Notu</h4>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-slate-600 mb-1">Görüşme Tarihi</label>
                    <input type="date" name="date" className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#1e3a5f]/20 bg-white" />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-600 mb-1">Konu</label>
                    <input type="text" name="subject" placeholder="Görüşme konusu..." className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#1e3a5f]/20 bg-white" />
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1">Notlar</label>
                  <textarea name="notes" rows={3} maxLength={500} placeholder="Görüşme detayları..." className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#1e3a5f]/20 resize-none bg-white" />
                </div>
                <button type="submit" className="px-5 py-2 rounded-lg bg-[#1e3a5f] text-white text-sm font-medium hover:bg-[#162d4a] cursor-pointer">Kaydet</button>
              </form>

              {veliNotlari.length === 0 ? (
                <div className="text-center py-8">
                  <i className="ri-phone-line text-4xl text-slate-200 block mb-2" />
                  <p className="text-slate-400 text-sm">Veli görüşme notu yok</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {veliNotlari.map((v) => (
                    <div key={v.id} className="border border-slate-100 rounded-xl p-4">
                      <div className="flex items-center justify-between mb-2">
                        <span className="font-medium text-slate-800">{v.gorusme_konusu || 'Genel Görüşme'}</span>
                        <span className="text-xs text-slate-400">{v.gorusme_tarihi || v.olusturma_tarihi?.split('T')[0]}</span>
                      </div>
                      <p className="text-sm text-slate-600">{v.notlar || '—'}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}